import { 
  collection, 
  doc, 
  addDoc, 
  setDoc, 
  getDoc, 
  getDocs, 
  updateDoc, 
  deleteDoc,
  query,
  where,
  orderBy
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from './config';
import {
  createStorageMetadata,
  recordDocumentMetadata,
  type DocumentUploadContext,
} from './documentRegistry';
import { sanitizeFileName } from '@/utils/fileNameSanitizer';
import { handleFirebaseAuthError } from '@/utils/firebaseErrorHandler';
import { auditLogService } from '@/services/auditLogService';

export interface EvidenceType {
  type: string;
  label: string;
  required?: boolean;
}

export interface ReferenceDocument {
  url: string;
  name: string;
  type: 'pdf' | 'image';
  uploadedAt?: string;
}

export interface ComplianceTemplate {
  id?: string;
  name: string;
  name_es?: string; // Spanish translation of name
  category: string;
  frequency: string;
  lead_times_days: number[];
  owner_role: string;
  evidence_types: EvidenceType[];
  pass_fail_criteria: string;
  pass_fail_criteria_es?: string; // Spanish translation of pass_fail_criteria
  jurisdiction: string;
  jurisdiction_notes: string;
  jurisdiction_notes_es?: string; // Spanish translation of jurisdiction_notes
  risk_level: string;
  risk_weight?: number; // Calculated from risk_level: HIGH=1.0, MEDIUM=0.6, LOW=0.3
  referenceDocuments?: ReferenceDocument[];
  mark_due_in_days?: number; // Number of days from now when compliance should be first marked as due
  organizationId?: string; // Multi-tenant support
  created_at?: any;
  updated_at?: any;
}

// Helper function to calculate risk weight from risk level
export function calculateRiskWeight(riskLevel: string): number {
  const normalized = riskLevel.toUpperCase().trim();
  if (normalized === 'HIGH') return 1.0;
  if (normalized === 'MEDIUM') return 0.6;
  if (normalized === 'LOW') return 0.3;
  // Default to medium if unknown
  return 0.6;
}

export interface ReferenceDocumentUploadOptions extends Partial<DocumentUploadContext> {
  templateId?: string;
}

// Upload reference document to Firebase Storage and get URL
export async function uploadReferenceDocument(
  file: File,
  options: ReferenceDocumentUploadOptions = {}
): Promise<ReferenceDocument> {
  // Validate file size before attempting upload
  const { validateFileSize } = await import('@/utils/fileSizeValidation');
  validateFileSize(file);

  if (!storage) {
    throw new Error('Firebase Storage not initialized. Please check your Firebase configuration.');
  }

  try {
    const { templateId, organizationId, uploadedBy, locationId } = options;
    const docType = options.docType || 'template_reference';
    
    // Fetch owner_role from the compliance template if templateId is provided
    let ownerRole: string | undefined = options.additionalMetadata?.owner_role as string | undefined;
    if (!ownerRole && templateId) {
      try {
        // Use dynamic import to avoid circular dependency
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('./config');
        const templateRef = doc(db, 'compliance_templates', templateId);
        const templateSnap = await getDoc(templateRef);
        if (templateSnap.exists()) {
          const templateData = templateSnap.data();
          ownerRole = templateData.owner_role;
        }
      } catch (error) {
        console.warn('Error fetching compliance template for owner_role:', error);
        // Continue without owner_role if template fetch fails
      }
    }
    
    // Validate file type
    const isPdf = file.type === 'application/pdf';
    const isImage = file.type.startsWith('image/');
    
    if (!isPdf && !isImage) {
      throw new Error('Only PDF and image files are allowed for reference documents.');
    }

    // Create a unique filename with timestamp
    const timestamp = Date.now();
    const folder = templateId ? `reference_documents/${templateId}` : 'reference_documents/general';
    const sanitizedFileName = sanitizeFileName(file.name);
    const fileName = `${folder}/${timestamp}_${sanitizedFileName}`;
    
    console.log('[ComplianceService] Filename sanitization', {
      original: file.name,
      sanitized: sanitizedFileName,
      finalPath: fileName,
    });
    
    // Create a storage reference
    const storageRef = ref(storage, fileName);
    const uploadedAt = new Date().toISOString();
    const storageContext: DocumentUploadContext = {
      docType,
      organizationId,
      templateId,
      locationId,
      uploadedBy,
      title: options.title || file.name,
      source: options.source || 'web',
      additionalMetadata: {
        ...options.additionalMetadata,
        ...(ownerRole && { owner_role: ownerRole }),
      },
    };
    
    // Upload the file
    await uploadBytes(
      storageRef,
      file,
      createStorageMetadata(storageContext, {
        contentType: file.type,
        uploadedAt,
      })
    );
    
    // Get the download URL
    const downloadURL = await getDownloadURL(storageRef);

    await recordDocumentMetadata({
      storagePath: storageRef.fullPath,
      bucket: (storageRef as any).bucket,
      downloadUrl: downloadURL,
      mimeType: file.type,
      sizeBytes: file.size,
      uploadedAt,
      context: storageContext,
    });
    
    return {
      url: downloadURL,
      name: file.name,
      type: isPdf ? 'pdf' : 'image',
      uploadedAt: new Date().toISOString()
    };
  } catch (error: any) {
    console.error('Error uploading reference document:', error);
    throw new Error(`File upload failed: ${error.message}`);
  }
}

// Compliance service
export const complianceService = {
  // Get all compliance templates for an organization
  getAllTemplates: async (organizationId?: string) => {
    try {
      const templatesRef = collection(db, 'compliance_templates');
      
      // Build query with organization filter if provided
      let q;
      if (organizationId) {
        // Filter by organizationId without orderBy to avoid index requirement
        q = query(
          templatesRef,
          where('organizationId', '==', organizationId)
        );
      } else {
        q = query(templatesRef, orderBy('name'));
      }
      
      const snapshot = await getDocs(q);
      
      const templates: ComplianceTemplate[] = [];
      const templatesToUpdate: Array<{ id: string; risk_weight: number }> = [];
      
      snapshot.forEach((doc) => {
        const templateData = doc.data();
        const template = { id: doc.id, ...templateData } as ComplianceTemplate;
        
        // Backward compatibility: calculate risk_weight if missing
        if (template.risk_weight === undefined || template.risk_weight === null) {
          const calculatedWeight = calculateRiskWeight(template.risk_level);
          template.risk_weight = calculatedWeight;
          templatesToUpdate.push({ id: doc.id, risk_weight: calculatedWeight });
        }
        
        templates.push(template);
      });
      
      // Update templates in database that are missing risk_weight (async, don't block)
      if (templatesToUpdate.length > 0) {
        // Use existing imports (doc and updateDoc are already imported at top)
        templatesToUpdate.forEach(async ({ id, risk_weight }) => {
          try {
            const templateRef = doc(db, 'compliance_templates', id);
            await updateDoc(templateRef, { risk_weight });
            console.log(`✅ Updated template ${id} with risk_weight: ${risk_weight}`);
          } catch (error) {
            console.error(`Error updating risk_weight for template ${id}:`, error);
          }
        });
      }
      
      // Sort in memory after fetching to avoid index requirement
      if (organizationId) {
        templates.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      }
      
      return { success: true, data: templates };
    } catch (error: any) {
      console.error('Error getting templates:', error);
      // Handle auth errors (token expiration, etc.)
      await handleFirebaseAuthError(error);
      return { success: false, error: error.message };
    }
  },

  // Get single template by ID
  getTemplate: async (templateId: string) => {
    try {
      const templateRef = doc(db, 'compliance_templates', templateId);
      const snapshot = await getDoc(templateRef);
      
      if (snapshot.exists()) {
        const templateData = snapshot.data();
        const template = { id: snapshot.id, ...templateData } as ComplianceTemplate;
        
        // Backward compatibility: calculate risk_weight if missing
        if (template.risk_weight === undefined || template.risk_weight === null) {
          const calculatedWeight = calculateRiskWeight(template.risk_level);
          template.risk_weight = calculatedWeight;
          
          // Update in database (async, don't block)
          updateDoc(templateRef, { risk_weight: calculatedWeight }).catch(error => {
            console.error(`Error updating risk_weight for template ${templateId}:`, error);
          });
        }
        
        return { success: true, data: template };
      } else {
        return { success: false, error: 'Template not found' };
      }
    } catch (error: any) {
      console.error('Error getting template:', error);
      return { success: false, error: error.message };
    }
  },

  // Add new template
  addTemplate: async (
    template: Omit<ComplianceTemplate, 'id'>, 
    organizationId?: string,
    userInfo?: { id: string; name: string; email: string }
  ) => {
    try {
      const templatesRef = collection(db, 'compliance_templates');
      // Ensure risk_weight is calculated if not provided
      const riskWeight = template.risk_weight ?? calculateRiskWeight(template.risk_level);
      const templateData: any = {
        ...template,
        risk_weight: riskWeight,
        ...(organizationId && { organizationId }), // Add organizationId if provided
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      Object.keys(templateData).forEach((key) => {
        if (templateData[key] === undefined) {
          delete templateData[key];
        }
      });
      const docRef = await addDoc(templatesRef, templateData);
      
      // Log audit entry
      if (userInfo && organizationId) {
        await auditLogService.log({
          action: 'compliance_added',
          entityType: 'compliance',
          entityId: docRef.id,
          entityName: template.name,
          userId: userInfo.id,
          userName: userInfo.name,
          userEmail: userInfo.email,
          organizationId: organizationId,
        });
      }
      
      return { success: true, id: docRef.id };
    } catch (error: any) {
      console.error('Error adding template:', error);
      return { success: false, error: error.message };
    }
  },

  // Update template
  updateTemplate: async (
    templateId: string, 
    template: Partial<ComplianceTemplate>,
    userInfo?: { id: string; name: string; email: string },
    organizationId?: string
  ) => {
    try {
      const templateRef = doc(db, 'compliance_templates', templateId);
      
      // Get current template for audit log
      let currentTemplate: ComplianceTemplate | null = null;
      if (userInfo && organizationId) {
        const currentResult = await complianceService.getTemplate(templateId);
        if (currentResult.success && currentResult.data) {
          currentTemplate = currentResult.data;
        }
      }
      
      // Ensure risk_weight is calculated if risk_level is being updated
      const updateData: any = { ...template };
      if (template.risk_level !== undefined && template.risk_weight === undefined) {
        updateData.risk_weight = calculateRiskWeight(template.risk_level);
      }
      Object.keys(updateData).forEach((key) => {
        if (updateData[key] === undefined) {
          delete updateData[key];
        }
      });
      await updateDoc(templateRef, {
        ...updateData,
        updated_at: new Date().toISOString()
      });
      
      // Log audit entry
      if (userInfo && organizationId && currentTemplate) {
        await auditLogService.log({
          action: 'compliance_updated',
          entityType: 'compliance',
          entityId: templateId,
          entityName: template.name || currentTemplate.name,
          userId: userInfo.id,
          userName: userInfo.name,
          userEmail: userInfo.email,
          organizationId: organizationId,
          details: {
            oldValue: currentTemplate,
            newValue: { ...currentTemplate, ...updateData },
          },
        });
      }
      
      return { success: true };
    } catch (error: any) {
      console.error('Error updating template:', error);
      return { success: false, error: error.message };
    }
  },

  // Delete template
  deleteTemplate: async (
    templateId: string,
    userInfo?: { id: string; name: string; email: string },
    organizationId?: string
  ) => {
    try {
      // Get template info before deletion for audit log
      let templateName = 'Unknown';
      if (userInfo && organizationId) {
        const templateResult = await complianceService.getTemplate(templateId);
        if (templateResult.success && templateResult.data) {
          templateName = templateResult.data.name;
        }
      }
      
      const templateRef = doc(db, 'compliance_templates', templateId);
      await deleteDoc(templateRef);
      
      // Log audit entry
      if (userInfo && organizationId) {
        await auditLogService.log({
          action: 'compliance_deleted',
          entityType: 'compliance',
          entityId: templateId,
          entityName: templateName,
          userId: userInfo.id,
          userName: userInfo.name,
          userEmail: userInfo.email,
          organizationId: organizationId,
        });
      }
      
      return { success: true };
    } catch (error: any) {
      console.error('Error deleting template:', error);
      return { success: false, error: error.message };
    }
  },

  // Copy/clone template
  copyTemplate: async (templateId: string) => {
    try {
      // Get original template
      const templateResult = await complianceService.getTemplate(templateId);
      
      if (!templateResult.success || !templateResult.data) {
        return { success: false, error: 'Template not found' };
      }
      
      const template = templateResult.data;
      
      // Remove ID and create new template with copied name
      const { id, ...templateData } = template;
      // Ensure risk_weight is set (recalculate if not present)
      const riskWeight = templateData.risk_weight ?? calculateRiskWeight(templateData.risk_level);
      const newTemplate = {
        ...templateData,
        risk_weight: riskWeight,
        name: `${template.name} (Copy)`,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      
      return await complianceService.addTemplate(newTemplate);
    } catch (error: any) {
      console.error('Error copying template:', error);
      return { success: false, error: error.message };
    }
  },

  // Import multiple templates (for JSON import)
  importTemplates: async (templates: Omit<ComplianceTemplate, 'id' | 'created_at' | 'updated_at'>[]) => {
    try {
      const results = [];
      
      for (const template of templates) {
        // Ensure risk_weight is calculated for imported templates
        const templateWithRiskWeight = {
          ...template,
          risk_weight: template.risk_weight ?? calculateRiskWeight(template.risk_level)
        };
        const result = await complianceService.addTemplate(templateWithRiskWeight);
        results.push(result);
      }
      
      const successCount = results.filter(r => r.success).length;
      const failCount = results.filter(r => !r.success).length;
      
      return {
        success: true,
        imported: successCount,
        failed: failCount,
        results
      };
    } catch (error: any) {
      console.error('Error importing templates:', error);
      return { success: false, error: error.message };
    }
  },

  // Get templates by category
  getTemplatesByCategory: async (category: string) => {
    try {
      const templatesResult = await complianceService.getAllTemplates();
      
      if (!templatesResult.success || !templatesResult.data) {
        return { success: false, error: 'Error fetching templates' };
      }
      
      const filtered = templatesResult.data.filter(t => t.category === category);
      return { success: true, data: filtered };
    } catch (error: any) {
      console.error('Error getting templates by category:', error);
      // Handle auth errors (token expiration, etc.)
      await handleFirebaseAuthError(error);
      return { success: false, error: error.message };
    }
  },

  // Search templates
  searchTemplates: async (searchTerm: string) => {
    try {
      const templatesResult = await complianceService.getAllTemplates();
      
      if (!templatesResult.success || !templatesResult.data) {
        return { success: false, error: 'Error fetching templates' };
      }
      
      const term = searchTerm.toLowerCase();
      const filtered = templatesResult.data.filter(t =>
        t.name.toLowerCase().includes(term) ||
        t.category.toLowerCase().includes(term) ||
        t.owner_role.toLowerCase().includes(term) ||
        t.jurisdiction.toLowerCase().includes(term)
      );
      
      return { success: true, data: filtered };
    } catch (error: any) {
      console.error('Error searching templates:', error);
      return { success: false, error: error.message };
    }
  }
};

